import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  describeIssue,
  duration,
  EmployeeTimekeepingSummary,
  getEmployeeTimekeepingSummaryApi,
  SIGNATURE_BADGE,
  SIGNATURE_LABELS,
} from "../api/timekeepingApi";
import { DP_TIMESHEET_PAGE } from "../routes";
import { MONTH_LABELS } from "../utils/format";
import BulkTimeAdjustmentModal from "./BulkTimeAdjustmentModal";
import FaceEnrollmentCard from "./FaceEnrollmentCard";
import "../pages/Timekeeping.css";

type Props = {
  token: string;
  employeeId: string;
  employeeName: string;
};

const iso = (value: string) => value.slice(0, 10);

/**
 * Aba Ponto da ficha: o mês corrente e o anterior (totais, situação do espelho,
 * assinatura do colaborador e pendências), atalhos para o espelho e para o lote,
 * e o cadastro do rosto para o quiosque. O aviso de acesso ao app criado depois da
 * admissão saiu: o ponto não depende mais do app (ADR 0032).
 */
export default function EmployeeTimekeepingTab({ token, employeeId, employeeName }: Props) {
  const navigate = useNavigate();
  const [data, setData] = useState<EmployeeTimekeepingSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bulk, setBulk] = useState<{ contractId: string; from: string; to: string } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await getEmployeeTimekeepingSummaryApi(token, employeeId));
    } catch (e: any) {
      setError(e?.message || "Erro ao carregar o ponto.");
    }
  }, [token, employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <p className="dpNotice dpNotice--warning">{error}</p>;
  if (!data) return <div className="dpEmpty">Carregando...</div>;
  if (!data.enabled) {
    return <p className="dpNotice">O ponto eletrônico ainda não foi ligado (Administração do sistema → Configurações → Ponto).</p>;
  }

  return (
    <div className="dpSection">
      <FaceEnrollmentCard token={token} employeeId={employeeId} employeeName={employeeName} />

      <div className="dpTkMonths">
        {data.months.map((m) => {
          const t = m.timesheet;
          return (
            <article key={`${m.year}-${m.month}`} className="dpTkCard">
              <div className="dpTkCard__head">
                <strong>
                  {MONTH_LABELS[m.month - 1]} de {m.year}
                </strong>
                {t ? (
                  <span className={`dpBadge ${t.status === "FECHADO" ? "dpBadge--green" : t.blockers.length ? "dpBadge--orange" : "dpBadge--blue"}`}>
                    {t.status === "FECHADO" ? "Fechado" : t.blockers.length ? "Aberto com bloqueio" : "Aberto"}
                  </span>
                ) : null}
              </div>
              {!t ? (
                <p className="dpMuted">Sem ponto neste mês.</p>
              ) : (
                <>
                  <div className="dpTkCard__grid">
                    <div>
                      <span>Trabalhado / previsto</span>
                      {duration(t.workedMinutes)} / {duration(t.expectedMinutes)}
                    </div>
                    <div>
                      <span>Horas extras</span>
                      {duration(t.overtimeWorkdayMinutes + t.overtimeRestDayMinutes)}
                    </div>
                    <div>
                      <span>Faltas (DSR)</span>
                      {t.absenceDays}
                      {t.dsrLostCount ? ` (${t.dsrLostCount})` : ""}
                    </div>
                    <div>
                      <span>Atrasos</span>
                      {duration(t.lateMinutes + t.earlyLeaveMinutes)}
                    </div>
                    <div>
                      <span>Faltas seguidas</span>
                      {t.maxConsecutiveAbsences}
                      {t.maxConsecutiveAbsences >= 30 ? " — possível abandono" : ""}
                    </div>
                    <div>
                      <span>Pedidos pendentes</span>
                      {t.pendingAdjustments}
                    </div>
                  </div>
                  {t.status === "FECHADO" || t.signature.status === "DESATUALIZADO" ? (
                    <div>
                      <span className="dpMuted">Assinatura: </span>
                      <span className={`dpBadge ${SIGNATURE_BADGE[t.signature.status]}`}>{SIGNATURE_LABELS[t.signature.status]}</span>
                      {t.signature.overdue ? <span className="dpMuted"> · há {t.signature.daysPending} dias</span> : null}
                      {t.signature.lastContest ? <div className="dpMuted">"{t.signature.lastContest.text}"</div> : null}
                    </div>
                  ) : null}
                  {t.blockers.length ? (
                    <div className="dpTkIssues">
                      {t.blockers.map((code) => (
                        <span key={code} className="dpTkFlag">
                          {describeIssue(code)}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <div className="dpTkActions">
                    {m.contractId ? (
                      <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => navigate(DP_TIMESHEET_PAGE(m.contractId as string, m.year, m.month))}>
                        Abrir espelho
                      </button>
                    ) : null}
                    {m.contractId && t.status === "ABERTO" ? (
                      <button
                        type="button"
                        className="dpBtn dpBtn--secondary dpBtn--small"
                        onClick={() => {
                          const first = `${m.year}-${String(m.month).padStart(2, "0")}-01`;
                          setBulk({ contractId: m.contractId as string, from: first, to: first });
                        }}
                      >
                        Lançar em lote
                      </button>
                    ) : null}
                  </div>
                </>
              )}
            </article>
          );
        })}
      </div>

      {bulk ? (
        <BulkTimeAdjustmentModal
          token={token}
          contractId={bulk.contractId}
          employeeName={employeeName}
          defaultFrom={bulk.from}
          defaultTo={bulk.to}
          suggestNoCredential={!!data.provisionedAt && iso(data.provisionedAt) > bulk.from}
          onClose={() => setBulk(null)}
          onSaved={() => {
            setBulk(null);
            toast.success("Lote lançado.");
            load();
          }}
        />
      ) : null}
    </div>
  );
}
